import React from 'react';
import { User, Bot, Send, UserCheck, ArrowRight, DollarSign, CheckCircle } from 'lucide-react';

const ACTION_LABELS = {
  creacion:                     { label: 'Solicitud Ingresada',              color: '#2563eb', bg: '#eff6ff' },
  cambio_estado:                { label: 'Cambio de Estado',                 color: '#7c3aed', bg: '#f5f3ff' },
  asignacion:                   { label: 'Solicitud Asignada',               color: '#0284c7', bg: '#f0f9ff' },
  cambio_estado_y_asignacion:   { label: 'Cambio de Estado y Asignación',    color: '#6366f1', bg: '#eef2ff' },
  envio_cotizacion:             { label: 'Cotización Emitida',               color: '#d97706', bg: '#fffbeb' },
  confirmacion_pago:            { label: 'Pago Confirmado',                  color: '#059669', bg: '#ecfdf5' },
  rechazo_pago:                 { label: 'Pago Rechazado',                   color: '#dc2626', bg: '#fef2f2' },
  devolucion:                   { label: 'Devolución de Fondos',             color: '#d97706', bg: '#fffbeb' },
  adjunto_documento:            { label: 'Documento Adjuntado',              color: '#059669', bg: '#ecfdf5' },
  correo_enviado:               { label: 'Correo Enviado al Ciudadano',      color: '#d97706', bg: '#fffbeb' },
  respuesta_ciudadano:          { label: 'Respuesta del Ciudadano',          color: '#0891b2', bg: '#ecfeff' },
  rechazo:                      { label: 'Solicitud Rechazada',              color: '#dc2626', bg: '#fef2f2' },
  actualizacion:                { label: 'Actualización de Datos',           color: '#64748b', bg: '#f1f5f9' },
};

function formatWorkerName(worker, payload = {}) {
  if (payload?.usuario || payload?.funcionario) {
    const u = String(payload.usuario || payload.funcionario).trim();
    if (u.toLowerCase().includes('admin')) return 'Sistema';
    return u;
  }
  if (!worker) return 'Sistema';
  const name = (typeof worker === 'string' ? worker : (worker.name || '')).trim();
  const lower = name.toLowerCase();
  const role = (worker.role || '').toLowerCase();
  if (!name || lower.includes('admin') || role === 'admin' || role === 'super_admin' || worker.id === 1) {
    return 'Sistema';
  }
  return name;
}

export default function HistorialPanel({ steps = [], onReply, usuarios = [], estados = [] }) {
  if (!steps.length) {
    return <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Sin historial registrado.</p>;
  }

  const resolveEstado = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && isNaN(Number(val))) return val;
    const match = estados.find(e => String(e.id) === String(val));
    return match ? match.nombre : (typeof val === 'string' ? val : `Estado #${val}`);
  };

  const resolveUsuario = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && isNaN(Number(val))) return val;
    const match = usuarios.find(u => String(u.id) === String(val));
    return match ? match.name : (typeof val === 'string' ? val : `Usuario #${val}`);
  };
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {steps.map(step => {
        const meta = ACTION_LABELS[step.action] || ACTION_LABELS.actualizacion;
        const esCiudadano = step.action === 'respuesta_ciudadano';
        
        return (
          <div key={step.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {esCiudadano
                ? <User size={15} color={meta.color} />
                : <Bot size={15} color={meta.color} />}
            </div>
            <div style={{
              flex: 1, background: meta.bg, borderRadius: 10, padding: '0.6rem 0.9rem',
              border: `1px solid ${meta.color}22`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: '0.78rem', color: meta.color }}>
                  {meta.label}
                </span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {new Date(step.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                  {!esCiudadano && (
                    <span style={{ marginLeft: 6 }}>
                      · {step.display_worker_name || formatWorkerName(step.worker, step.payload)}
                    </span>
                  )}
                </span>
              </div>
              {step.payload?.asunto && (
                <div style={{ fontSize: '0.82rem', marginBottom: 2 }}>
                  <strong>Asunto:</strong> {step.payload.asunto}
                </div>
              )}
              {(step.payload?.mensaje || step.payload?.comentario) && (
                <div style={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                  {step.payload.mensaje || step.payload.comentario}
                </div>
              )}
              {step.payload?.nota && (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  {step.payload.nota}
                </div>
              )}

              {/* Detalle de Asignación */}
              {(step.action === 'asignacion' || step.action === 'cambio_estado_y_asignacion' || step.payload?.changes?.asignado_a) && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                  <UserCheck size={13} style={{ color: '#0284c7', flexShrink: 0 }} />
                  <span>
                    <strong>Asignado a:</strong>{' '}
                    <strong style={{ color: '#0369a1' }}>
                      {resolveUsuario(step.payload?.changes?.asignado_a?.new || step.payload?.funcionario || 'Funcionario')}
                    </strong>
                    {step.payload?.changes?.asignado_a?.old && step.payload?.changes?.asignado_a?.old !== 'Sin asignar' && (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.74rem', marginLeft: 6 }}>
                        (anteriormente: {resolveUsuario(step.payload.changes.asignado_a.old)})
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Cambio de Estado */}
              {(step.action === 'cambio_estado' || step.action === 'cambio_estado_y_asignacion' || step.payload?.changes?.estado) && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                  <ArrowRight size={12} style={{ color: '#7c3aed', flexShrink: 0 }} />
                  <span>
                    {step.payload?.changes?.estado ? (
                      <>
                        <span style={{ color: 'var(--text-muted)' }}>{resolveEstado(step.payload.changes.estado.old)}</span>
                        <span style={{ margin: '0 4px', color: '#7c3aed', fontWeight: 700 }}>➔</span>
                        <strong style={{ color: '#5b21b6' }}>{resolveEstado(step.payload.changes.estado.new)}</strong>
                      </>
                    ) : (step.old_state && step.new_state && step.old_state !== step.new_state) ? (
                      <>
                        <span style={{ color: 'var(--text-muted)' }}>{resolveEstado(step.old_state)}</span>
                        <span style={{ margin: '0 4px', color: '#7c3aed', fontWeight: 700 }}>➔</span>
                        <strong style={{ color: '#5b21b6' }}>{resolveEstado(step.new_state)}</strong>
                      </>
                    ) : (
                      <strong>{resolveEstado(step.new_state) || 'Estado actualizado'}</strong>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Cotización */}
              {step.action === 'envio_cotizacion' && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
                  <DollarSign size={13} style={{ color: '#d97706', flexShrink: 0 }} />
                  <span>
                    <strong>Total Cotizado:</strong> ${Number(step.payload?.total || 0).toLocaleString('es-CL')}
                    {step.payload?.caja && (
                      <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        · Caja: {step.payload.caja}
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Confirmación de Pago */}
              {step.action === 'confirmacion_pago' && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#059669' }}>
                  <CheckCircle size={13} style={{ flexShrink: 0 }} />
                  <span>
                    <strong>Monto confirmado:</strong> ${Number(step.payload?.total || step.payload?.monto || 0).toLocaleString('es-CL')}
                  </span>
                </div>
              )}

              {/* Otros campos modificados */}
              {step.payload?.changes && Object.keys(step.payload.changes).filter(k => !['estado', 'estado_id', 'asignado_a'].includes(k)).length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem' }}>
                  {Object.entries(step.payload.changes)
                    .filter(([k]) => !['estado', 'estado_id', 'asignado_a'].includes(k))
                    .map(([field, val]) => (
                      <div key={field} style={{ background: '#ffffff', padding: '3px 8px', borderRadius: 4, border: '1px solid #e2e8f0', color: '#334155' }}>
                        <strong style={{ textTransform: 'capitalize' }}>{field.replace(/_/g, ' ')}:</strong>{' '}
                        <span style={{ color: '#94a3b8', textDecoration: 'line-through' }}>{String(val?.old ?? '—')}</span>
                        <span style={{ margin: '0 4px', color: '#64748b' }}>➔</span>
                        <strong style={{ color: '#0f172a' }}>{String(val?.new ?? '—')}</strong>
                      </div>
                    ))}
                </div>
              )}

              {/* Comentarios de auditoría */}
              {step.comments && !step.payload?.mensaje && !step.payload?.comentario && (
                <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: 4, fontStyle: 'italic' }}>
                  {step.comments}
                </div>
              )}

              {esCiudadano && onReply && (
                <div style={{ marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                   <button
                    className="btn btn-sm"
                    style={{
                      padding: '2px 10px', fontSize: '0.7rem', background: 'white',
                      color: meta.color, border: `1px solid ${meta.color}`,
                      display: 'flex', alignItems: 'center', gap: 4
                    }}
                    onClick={() => onReply(step)}
                   >
                     <Send size={10} /> Responder
                   </button>
                </div>
              )}

              {step.payload?.archivos?.length > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: '1.2rem', fontSize: '0.78rem', color: '#374151' }}>
                  {step.payload.archivos.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
